import _ from 'lodash'
import qc from 'qc'
import { scripts } from '@ps/bridge/qc/scripts'
import * as editorScript from '@ps/bridge/qc/scripts/editor'
import { getNodeOrigin, nodeOrigin } from '@ps/bridge/qc/scripts/editor'
import { Ib } from '@ps/utils/editor.js'
import { getDescendants } from '@ps/bridge/qc/node/utils'
import * as videoScript from '@ps/bridge/qc/scripts/video'
import nodePool from '@ps/bridge/qc/node-pool'
import gameIframe from '@ps/bridge/iframe/iframe'
import iframe from '@ps/bridge/iframe/iframe'
import * as langScript from '@ps/bridge/qc/scripts/language'
import { getSwitchSceneEventData } from '@ps/bridge/qc/vp'
import { nodeTypes } from '@ps/bridge/qc/scripts/editor'
import { checkIsSupportDragBound } from '@ps/store/workbench/visual-program/utils'
import { quickLayoutComponent } from '@ps/utils/config'
import { isShelvesNode } from '@ps/workbench/undo-redo/module/shelves'

export const UITextClass = 'qc.UIText'
export const UIImageClass = 'qc.UIImage'
export const UIRootClass = 'qc.UIRoot'
export const UINodeClass = 'qc.Node'
export const MovieClipClass = scripts.movieClip.name
export const TweenClass = 'qc.Tween'
export const TweenPositionClass = 'qc.TweenPosition'
export const VideoClass = scripts.video.name
export const AudioClass = scripts.audio.name
export const GlobalConfigBgClass = 'ps.GlobalConfigBg'
export const DragTargetAreaClass = 'ps.DragTarget'
export const DragBoundClass = 'ps.DragBound'
export const NodeGhostClass = 'ps.NodeGhost'
export const VPGamePlayClass = 'ps.VPGamePlay'
export const ProgressBarClass = 'qc.ProgressBar'

export const isText = (node: qc.Node): boolean => {
  return Boolean(node && node.class === UITextClass)
}

export const isRoot = (node: qc.Node): boolean => {
  return Boolean(node && node.class === UIRootClass)
}

export const isSequenceFrame = (node: qc.Node): boolean => {
  return Boolean(
    node && node.class === UIImageClass && node.getScript(MovieClipClass),
  )
}

export const isVideo = (node: qc.Node): boolean => {
  return Boolean(
    node && node.scripts && node.scripts.some((e) => e.class === VideoClass),
  )
}

export const isAudio = (node: qc.Node): boolean => {
  return Boolean(
    node && node.scripts && node.scripts.some((e) => e.class === AudioClass),
  )
}

/**
 * 是否是九宫格图片
 */
export const isSquaredUp = (node: qc.Node): boolean => {
  return node.imageType === 1
}

export const isParticle = (node: qc.Node) => {
  let result = false
  if (!node) return false
  if (
    !isNaN(node.maxParticles!) &&
    typeof Number(node.maxParticles) === 'number' &&
    !isNaN(Number(node.maxParticles))
  ) {
    result = true
  }
  return result
}

export const hasParticleNode = (node: qc.Node) => {
  if (isParticle(node)) {
    return true
  }
  for (const cNode of node.children || []) {
    if (hasParticleNode(cNode)) {
      return true
    }
  }
  return false
}

// 判断是否粒子元素父节点
export const isParticleParent = (node: qc.Node) => {
  if (!node || node.children.length === 0) return false
  const children = getDescendants(node, false, false)
  // 粒子父节点子孙节点必须为粒子节点或者包含粒子节点的组
  return children.every((item) => isParticle(item) || item.children.length)
}

export const isGroup = (node: qc.Node): boolean => {
  if (!node || !node.getScript) return false
  const data = editorScript.getScript(node)
  return data?.$data?.nodeType === nodeTypes.group
}
// 互动视频的组
export const isIvGroup = (node: qc.Node): boolean => {
  if (!node || !node.getScript) return false
  const data = editorScript.getScript(node)
  return data?.$data?.nodeType === nodeTypes.ivGroup
}

/**
 * 是否快捷布局节点
 * @param node
 * @returns
 */
export const isQuickLayout = (node: qc.Node): boolean => {
  if (!node || !node.getScript) return false
  const data = editorScript.getScript(node)
  return data?.$data?.nodeType === nodeTypes.quickLayout
}

export const isNodeGhost = (node: qc.Node): boolean => {
  if (!node || !node.getScript) return false
  const data = editorScript.getScript(node)
  return data?.$data?.nodeType === NodeGhostClass
}

export const isTempNode = (node: qc.Node): boolean => {
  if (!node || !node.getScript) return false
  const data = editorScript.getScript(node)
  return Boolean(data?.$data?.isTempNode)
}

export const isTempTextNode = (node: qc.Node): boolean => {
  if (!node || !node.getScript) return false
  const data = editorScript.getScript(node)
  return Boolean(data?.$data?.isTempNode && isText(node))
}

export const isHaloNode = (node: qc.Node): boolean => {
  const iframeWindow = gameIframe.iframe
  if (!node || !node.getScript) return false
  if (iframeWindow && iframeWindow.ps && iframeWindow.ps.Halo) {
    return Boolean(node.getScript('ps.Halo'))
  }
  return false
}
// 是否qc自带的图形节点
export const isGraphicsNode = (node: qc.Node): boolean => {
  return node.class === 'qc.Graphics'
}
/**
 * 是否是导入的节点（即不是在 ps 上添加的节点）
 * @param node
 */
export function isImportNode(node: qc.Node): boolean {
  return getNodeOrigin(node) === nodeOrigin.import
}

/**
 * 判断节点是否龙骨
 * @param {节点} node
 */
export function isDragonBone(node: qc.Node) {
  if (!node) {
    return false
  }
  const sk = node.getScript('ps.SKPlayer')
  if (sk) {
    return true
  }
  return false
}

/**
 * 判断节点是否龙骨子节点
 * @param {*} node
 */
export function isDragonBoneChild(node: qc.Node) {
  while (node && node.parent) {
    // @ts-ignore
    if (node.parent.getScript && isDragonBone(node.parent)) {
      return true
    }
    node = node.parent
  }
  return false
}

/**
 * 判断是否是导入的节点，并且有主动通过可视化操作挪动过位置
 * @param node
 */
export function isMovedImportNode(node: qc.Node): boolean {
  const data = editorScript.getScript(node)
  // 只有挪动过位置才会有 worldPosition
  return isImportNode(node) && Boolean(data?.$data.worldPosition)
}

export const isQcColor = (value: qc.Color): boolean => {
  return Boolean(value && value.class === 'qc.Color')
}

export const hadChildren = (node: qc.Node): boolean => {
  return Boolean(node && _.isArray(node.children) && node.children.length > 0)
}

export const hadAnimation = (node: qc.Node): boolean => {
  return Boolean(node && node.getScript(TweenClass))
}

export const hadPositionAnimation = (node: qc.Node): boolean => {
  // 青瓷底层有bug，删除了qc.TweenPosition后再getScript，还是存在qc.TweenPosition
  return Boolean(
    node && node.scripts.some((item) => item.class === TweenPositionClass),
  )
}

export const hadEventToScene = (node: qc.Node): boolean => {
  const switchSceneEventData = getSwitchSceneEventData()
  return !!switchSceneEventData.find((item) => item.nodeId === node.uuid)
}
// 节点是否有对应的事件
export const hadEvent = (node: qc.Node): boolean => {
  const sceneNode = getSceneNodeByNode(node)
  if (!sceneNode) return false
  const editorData = editorScript.getScript(sceneNode)
  if (!editorData) return false
  const vpAst = editorData.vpAst
  if (!vpAst) return false
  for (const i in vpAst) {
    if (vpAst[i] && vpAst[i]?.condition) {
      for (const j in vpAst[i]?.condition) {
        if (
          vpAst[i]?.condition[j] &&
          vpAst[i]?.condition[j].node === node.uuid
        ) {
          return true
        }
      }
    }
  }
  return false
}

export const isEmptyNode = (node: qc.Node): boolean => {
  return Boolean(
    node &&
      node.scripts &&
      node.scripts.some((e) => e.class === scripts.emptyNode.name),
  )
}

export const isDrawLineNode = (node: qc.Node): boolean => {
  return Boolean(
    node &&
      node.scripts &&
      node.scripts.some((e) => e.class === scripts.drawLine.name),
  )
}
export const isDrawLineAreaNode = (node: qc.Node): boolean => {
  return Boolean(
    node &&
      node.scripts &&
      node.scripts.some((e) => e.class === scripts.drawLineArea.name),
  )
}
export const isFreeDrawLineNode = (node: qc.Node): boolean => {
  return Boolean(
    node &&
      node.scripts &&
      node.scripts.some((e) => e.class === scripts.drawLine.name) &&
      node.getScript(scripts.drawLine.name).isFreeLine,
  )
}
export const isConnectDrawLineNode = (node: qc.Node): boolean => {
  return Boolean(
    node &&
      node.scripts &&
      node.scripts.some((e) => e.class === scripts.drawLine.name) &&
      node.getScript(scripts.drawLine.name).isConnectLine,
  )
}
export const isEraseNode = (node: qc.Node): boolean => {
  if (!node) {
    return false
  }
  const editorData = editorScript.getScript(node)
  return editorData?.$data.nodeType === nodeTypes.erase
}

export const isCarouselNode = (node: qc.Node): boolean => {
  if (!node) {
    return false
  }
  const editorData = editorScript.getScript(node)
  return editorData?.$data.nodeType === nodeTypes.carousel
}

export const isCarouselResourceGroupNode = (node: qc.Node): boolean => {
  if (!node) {
    return false
  }
  const editorData = editorScript.getScript(node)
  return editorData?.$data.nodeType === nodeTypes.carouselRes
}

export const isCarouselPiteGroupNode = (node: qc.Node): boolean => {
  if (!node) {
    return false
  }
  const editorData = editorScript.getScript(node)
  return editorData?.$data.nodeType === nodeTypes.carouselPite
}

export const isCarouselShadowNode = (node: qc.Node): boolean => {
  if (!node) {
    return false
  }
  const editorData = editorScript.getScript(node)
  return editorData?.$data.nodeType === nodeTypes.carouselShadow
}

export const isCarouselNodeChild = (node: qc.Node): boolean => {
  if (!node) return false
  const list: [qc.Node] = getAllParentsBeforeScene(node) as [qc.Node]
  return list && list.some((node) => isCarouselNode(node))
}

export const isCarouselArrowNode = (node: qc.Node): boolean => {
  if (!node) return false
  return ['Carousel prev', 'Carousel next'].some(
    (val) => node.name.indexOf(val) > -1,
  )
}
// 根据遮罩的子节点获取其遮罩组件的节点
export function getCarouselComponentNodeByChildNode(node: qc.Node) {
  // eslint-disable-next-line no-constant-condition
  while (true) {
    if (!node) {
      return null
    } else if (node.getScript('ps.VPGamePlay')) {
      return null
    } else if (isCarouselNode(node)) {
      return node
    }
    node = node.parent
  }
}

export const getCarouselNodeInScene = (
  sceneNode: qc.Node,
): qc.Node | undefined => {
  const nodes = getDescendants(sceneNode)
  return nodes.find((node) => isCarouselNode(node))
}

export const isCarouselGroup = (
  node: qc.Node,
  carouselNode?: qc.Node,
): boolean => {
  if (!node || !node.uuid) {
    return false
  }
  const nNode = nodePool.find(node.uuid)
  const editorData = editorScript.getScript(nNode)
  return editorData?.$data.nodeType === nodeTypes.carouselGroup
}

export const isCarouselResource = (
  node: qc.Node,
  carouselNode: qc.Node,
): boolean => {
  if (!node || !carouselNode) {
    return false
  }
  const carouselData = carouselNode.getScript(scripts.carousel.name)
  const carouselGroups = Object.values(carouselData.carouselGroups)
  const carouselResources: string[] = carouselGroups.reduce(
    (all, cur) => all.concat(cur.items),
    [],
  ) as string[]
  return carouselResources.includes(node.uuid)
}

export const isEraseCustomJudgeAreasNode = (node: qc.Node): boolean => {
  if (!node) return false
  const editorData = editorScript.getScript(node)
  return editorData?.$data.nodeType === nodeTypes.customJudgeAreas
}
// 停车解密组件
export const isParkNode = (node: qc.Node): boolean => {
  if (!node) {
    return false
  }
  const iframeWindow = gameIframe.iframe
  if (iframeWindow && iframeWindow.ps && iframeWindow.ps.ParkComponent) {
    const park = node.getScript(scripts.park.name)
    if (park) {
      return true
    }
  }
  return false
}

export const getParkNode = (sceneNode: qc.Node): qc.Node | undefined => {
  const nodes = getDescendants(sceneNode)
  return nodes.find((node) => isParkNode(node))
}
// 停车解密组件下的临时资源节点组
export const getParkResNode = (sceneNode: qc.Node): qc.Node | null => {
  const parkNode = getParkNode(sceneNode)
  if (!parkNode) return null
  let tempResourceNode = parkNode.getChild('tempResource')
  if (tempResourceNode) return tempResourceNode
  tempResourceNode = gameIframe.qcGame.add.node(parkNode)
  tempResourceNode.name = 'tempResource'
  tempResourceNode.visible = false
  const data = editorScript.getScript(tempResourceNode)
  data.$data.nodeType = NodeGhostClass
  return tempResourceNode
}

// 是否是受操作限制的节点, 如果是受操作限制的话，代表只允许在父级节点内进行操作
export const isRestrictedNode = (node: qc.Node): boolean => {
  return isDrawLineAreaNode(node)
}
export const isImage = (node: qc.Node, absoluteImage = true): boolean => {
  if (!node) return false
  // 手势区域、序列帧、音频的class也是qc.UIImage,qc.ProgressBar进度条也判定为图片
  if (absoluteImage) {
    return (
      (node.class === UIImageClass || node.class === ProgressBarClass) &&
      !isSequenceFrame(node) &&
      !isEmptyNode(node) &&
      !isAudio(node)
    )
  }
  return node.class === UIImageClass
}

/**
 * 判断节点是否多语言图片字
 */
export const isLangImage = (node: qc.Node): boolean => {
  return Boolean(isImage(node) && langScript.getScript(node, false))
}

// 是否拖拽区域
export const isDragTargetArea = (node: qc.Node): boolean => {
  return Boolean(node && node.getScript(DragTargetAreaClass))
}

// 是否拖拽范围
export const isDragBound = (node: qc.Node): boolean => {
  if (!checkIsSupportDragBound()) {
    return false
  }
  return Boolean(node && node.getScript(DragBoundClass))
}

export const isTweenPositionArea = (node: qc.Node): boolean => {
  const data = editorScript.getScript(node)
  return data?.$data?.nodeType === nodeTypes.tweenPositionArea
}

export const isScene = (node: qc.Node): boolean => {
  return Boolean(
    node && node.getScript && node.getScript(scripts.sceneConfig.name),
  )
}
// 互动视频热区节点
export const isIvHotAreaNode = (node: qc.Node): boolean => {
  const editorData = editorScript.getScript(node)
  return editorData?.$data.nodeType === nodeTypes.ivHotArea
}
// 互动视频结束页跳转热区节点
export const isIvEndingInstallNode = (node: qc.Node): boolean => {
  const editorData = editorScript.getScript(node)
  return editorData?.$data.nodeType === nodeTypes.ivEndingInstall
}

/**
 * 根据节点极坐标，计算父级layout
 */
export function getNodesLayout(
  group: qc.Node,
  nodes: qc.Node[],
  currentSceneNode: qc.Node,
): {
  x: number
  y: number
  width: number
  height: number
} {
  const positions: { x: number; y: number }[] = []

  nodes.forEach((node) => {
    const pointList: qc.Point[] = Ib(node) as qc.Point[]
    pointList.forEach((point) => {
      positions.push(group.parent.toLocal(point))
    })
  })
  const min = {
    x: _.min(positions.map((item) => item.x)) || 0,
    y: _.min(positions.map((item) => item.y)) || 0,
  }
  const max = {
    x: _.max(positions.map((item) => item.x)) || 0,
    y: _.max(positions.map((item) => item.y)) || 0,
  }

  // 中心坐标转左上角坐标
  const point = {
    x: min.x + (max.x - min.x) / 2,
    y: min.y + (max.y - min.y) / 2,
  }
  return {
    ...point,
    width: max.x - min.x,
    height: max.y - min.y,
  }
}

/**
 * > 如果两个节点的类型相同，则返回 true
 * @param node - 要检查的节点。
 * @param otherNode - 要比较的节点。
 */
export function isSameNodeType(node: qc.Node, otherNode: qc.Node) {
  const comparator = [
    isVideo,
    isAudio,
    isText,
    isSequenceFrame,
    isGroup,
    isEmptyNode,
    isDragonBone,
    isDragonBoneChild,
    isScene,
    isParticle,
    isParticleParent,
    // 图片节点放在最后，避免其他节点被判定为图片节点
    isImage,
  ]
  return comparator.some((item) => {
    return item(node) && item(otherNode)
  })
}

/**
 * 判断节点是否是一级节点
 * @param {*} node
 */
export function isFirstLevelNodeInScene(node: qc.Node) {
  if (
    node &&
    node.parent &&
    node.parent.parent &&
    node.parent.parent.name === 'gamePlay' &&
    node.parent.parent.parent === gameIframe.UIRoot
  ) {
    return true
  }
  return false
}

export interface paddingBorder {
  borderLeft: number | string
  borderTop: number | string
  borderRight: number | string
  borderBottom: number | string
}

/**
 * 九宫格图片解析border值
 * @param padding
 */
export function parsePadding(padding: paddingBorder): number[] {
  // 数组 左上右下
  return [
    Number(padding.borderLeft),
    Number(padding.borderTop),
    Number(padding.borderRight),
    Number(padding.borderBottom),
  ]
}

/**
 * 九宫格参数border是否被修改过
 * @param node 图片的节点
 */
export function checkNineIsModify(node: qc.Node): boolean {
  if (node.imageType !== 1) return false
  const paddingBorder = _.pick(node, [
    'borderTop',
    'borderLeft',
    'borderRight',
    'borderBottom',
  ]) as paddingBorder
  const nodePadding = parsePadding(paddingBorder)
  const binPadding = _.get(node, 'texture.atlas.meta.padding.0') || []
  return nodePadding.join(',') !== binPadding.join(',')
}

/**
 * 获取节点对应的资源的原始宽高
 * @param node
 */
export function getNativeSize(node: qc.Node): {
  width: number
  height: number
} | void {
  // 视频节点通过脚本获取
  if (isVideo(node)) {
    const videoScriptData = videoScript.getScript(node)
    if (videoScriptData) {
      return videoScriptData.$data.nativeSize
    }
  } else {
    return node.nativeSize
  }
}

/**
 * 使用节点 id 获取该节点对应的场景 id
 * @param id
 */
export function getSceneNodeByUuid(id: string): qc.Node | undefined {
  let node = nodePool.find(id)
  while (node) {
    if (isScene(node)) {
      return node
    }
    node = node.parent
  }
}

/**
 * 获取节点所在的场景节点
 * @param node
 */
export function getSceneNodeByNode(node: qc.Node) {
  if (!node || !node.parent) return null
  // eslint-disable-next-line no-constant-condition
  while (true) {
    if (!node.parent) {
      return null
    }
    if (node.parent.getScript('ps.VPGamePlay')) {
      break
    }
    node = node.parent
  }
  return node
}

/**
 *
 * @param node 获取节点和场景节点之间，所有父级节点
 * @returns
 */
export function getAllParentsBeforeScene(node: qc.Node) {
  let temp = nodePool.find(node.uuid)
  const parents = []
  while (temp) {
    if (isScene(temp)) {
      return parents
    }
    parents.push(temp.parent)
    temp = temp.parent
  }
}

/**
 * 过滤节点列表中有子节点、孙子节点，只保留列表中父节点
 * @param nodes
 * @returns
 */
export function filterRelatedNode(nodes: [qc.Node]) {
  const map: string[] = []
  nodes.forEach((node) => {
    map.push(
      getAllParentsBeforeScene(node)
        ?.map((item) => item.uuid)
        .join() as string,
    )
  })
  return nodes.filter((node, index) => {
    return nodes.every((item) => map[index]?.indexOf(item.uuid) === -1)
  })
}

export function haveFatherSonRelationship(
  father: qc.Node,
  son: qc.Node,
): boolean {
  const fatherNode = father
  let sonNode = son
  while (sonNode) {
    if (sonNode === fatherNode) {
      return true
    }
    sonNode = sonNode.parent
  }
  return false
}

/**
 * 它返回具有给定 id 的场景的索引。
 * @param {string} sceneId - 要获取其索引的场景的 ID。
 */
export function getSceneIndex(sceneId: string): number {
  return _.findIndex(iframe.gamePlay.children, (j) => j.uuid === sceneId)
}

/**
 * 它返回游戏中的下一个场景，如果没有下一个场景，则返回 undefined。
 * @param {string} sceneId - 要获取下一个场景的场景的 id。
 */
export function getNextScene(sceneId: string): qc.Node | undefined {
  const index = getSceneIndex(sceneId)
  if (index < 0) return
  return iframe.gamePlay.children[index + 1]
}

/**
 * 获取节点的世界坐标值
 * @param node
 * @param convert 是否需要换算，如果输出的值用于编辑器层面展示，则需要换算，除以游戏世界 scale
 */
export function getNodeWorldPosition(
  node: qc.Node,
  convert = true,
): { x: number; y: number } {
  const position = node.getWorldPosition()
  const worldScale = gameIframe.UIRoot.getWorldScale()
  return {
    x: convert ? position.x / worldScale.x : position.x,
    y: convert ? position.y / worldScale.y : position.y,
  }
}

export function convertCoordinateByWorldScale({
  x,
  y,
}: {
  x: number
  y: number
}): { x: number; y: number } {
  const worldScale = gameIframe.UIRoot.getWorldScale()
  return {
    x: x * worldScale.x,
    y: y * worldScale.y,
  }
}

/**
 * 支持添加动画的节点
 * @param node
 */
export const supportAnimationNode = (node: qc.Node): boolean => {
  return (
    (isImage(node) && !isDragonBoneChild(node)) ||
    isText(node) ||
    isDragonBone(node) ||
    isParticleParent(node) ||
    isSequenceFrame(node) ||
    isGroup(node) ||
    isParticle(node) ||
    isEraseNode(node)
  )
}

export const hasDrawLineInScene = (sceneNode: qc.Node): boolean => {
  return sceneNode.children.some((node: qc.Node) => isDrawLineNode(node))
}
// 是否有连线组件
export const hasDrawLineConnectInScene = (sceneNode: qc.Node): boolean => {
  return sceneNode.children.some((node: qc.Node) => {
    if (isDrawLineNode(node)) {
      const dlc = node.getScript(scripts.drawLine.name)
      return dlc.isConnectLine
    }
    return false
  })
}

// 是否有自由画线组件
export const hasDrawLineFreeInScene = (sceneNode: qc.Node): boolean => {
  return sceneNode.children.some((node: qc.Node) => {
    if (isDrawLineNode(node)) {
      const dlc = node.getScript(scripts.drawLine.name)
      return dlc.isFreeLine
    }
    return false
  })
}

// 是否有擦除组件
export const hasEraseNodeInScene = (sceneNode: qc.Node): boolean => {
  const nodes = getDescendants(sceneNode)
  return nodes.some((node) => isEraseNode(node))
}

// 是否有轮播组件
export const hasCarouselNodeInScene = (sceneNode: qc.Node): boolean => {
  const nodes = getDescendants(sceneNode)
  return nodes.some((node) => isCarouselNode(node))
}

// 根据轮播的子节点获取其组件的节点
export function getCarouselNodeByChildNode(node: qc.Node) {
  // eslint-disable-next-line no-constant-condition
  while (true) {
    if (node.getScript('ps.VPGamePlay')) {
      return null
    } else if (isCarouselNode(node)) {
      return node
    } else if (!node) {
      return null
    }
    node = node.parent
  }
}

export const isMaskComponentNode = (node: qc.Node): boolean => {
  if (!node) {
    return false
  }
  const editorData = editorScript.getScript(node)
  return editorData?.$data.nodeType === nodeTypes.mask
}

export const isMaskComponentNodeChild = (node: qc.Node): boolean => {
  if (!node) return false
  const list: [qc.Node] = getAllParentsBeforeScene(node) as [qc.Node]
  return list && list.some((node) => isMaskComponentNode(node))
}

// 是否有遮罩组件
export const hasMaskComponentNodeInScene = (sceneNode: qc.Node): boolean => {
  const nodes = getDescendants(sceneNode)
  return nodes.some((node) => isMaskComponentNode(node))
}

export const getMaskComponentNodeInScene = (
  sceneNode: qc.Node,
): qc.Node | undefined => {
  const nodes = getDescendants(sceneNode)
  return nodes.find((node) => isMaskComponentNode(node))
}

// 根据遮罩的子节点获取其遮罩组件的节点
export function getMaskComponentNodeByChildNode(node: qc.Node) {
  // eslint-disable-next-line no-constant-condition
  while (true) {
    if (!node) {
      return null
    } else if (node.getScript('ps.VPGamePlay')) {
      return null
    } else if (isMaskComponentNode(node)) {
      return node
    }
    node = node.parent
  }
}

export const getDrawLineInScene = (sceneNode: qc.Node): qc.Node | undefined => {
  return sceneNode.children.find((node: qc.Node) => isDrawLineNode(node))
}

export const getConnectDrawLineInScene = (
  sceneNode: qc.Node,
): qc.Node | undefined => {
  return sceneNode.children.find((node: qc.Node) => isConnectDrawLineNode(node))
}

export const getDrawLineAreaInScene = (sceneNode: qc.Node): qc.Node[] => {
  const drawLineNode = sceneNode.children.find((node: qc.Node) =>
    isDrawLineNode(node),
  )
  if (!drawLineNode) return []
  return drawLineNode?.children
}

export const getEraseNodeInScene = (
  sceneNode: qc.Node,
): qc.Node | undefined => {
  const nodes = getDescendants(sceneNode)
  return nodes.find((node) => isEraseNode(node))
}
// 根据擦除的子节点获取其擦除组件的节点
export function getEraseNodeByChildNode(node: qc.Node) {
  // eslint-disable-next-line no-constant-condition
  while (true) {
    if (!node) {
      return null
    } else if (node.getScript('ps.VPGamePlay')) {
      return null
    } else if (isEraseNode(node)) {
      return node
    }
    node = node.parent
  }
}

export const isEraseNodeChild = (node: qc.Node): boolean => {
  if (!node) return false
  const list: [qc.Node] = getAllParentsBeforeScene(node) as [qc.Node]
  return list && list.some((node) => isEraseNode(node))
}

// 擦除组件跟手图片
export const isEraseBrushImages = (node: qc.Node): boolean => {
  const iframeWindow = gameIframe.iframe
  if (iframeWindow && iframeWindow.ps && iframeWindow.ps.EraseComponent) {
    const eraseComponent = node.parent
      ? node.parent.getScript(scripts.erase.name)
      : null
    if (!eraseComponent) return false
    return eraseComponent.brushImages.uuid === node.uuid
  }
  return false
}

// 擦除组件笔刷
export const isEraseBrushCategory = (node: qc.Node): boolean => {
  const iframeWindow = gameIframe.iframe
  if (iframeWindow && iframeWindow.ps && iframeWindow.ps.EraseComponent) {
    const eraseComponent = node.parent
      ? node.parent.getScript(scripts.erase.name)
      : null
    if (!eraseComponent) return false
    return eraseComponent.brushCategory.uuid === node.uuid
  }
  return false
}

// 擦除组件底图
export const isEraseBottomImages = (node: qc.Node): boolean => {
  const iframeWindow = gameIframe.iframe
  if (iframeWindow && iframeWindow.ps && iframeWindow.ps.EraseComponent) {
    const eraseComponent = node.parent
      ? node.parent.getScript(scripts.erase.name)
      : null
    if (!eraseComponent) return false
    return eraseComponent.bottomImages.uuid === node.uuid
  }
  return false
}

// 擦除组件蒙层
export const isEraseMaskImage = (node: qc.Node): boolean => {
  const iframeWindow = gameIframe.iframe
  if (iframeWindow && iframeWindow.ps && iframeWindow.ps.EraseComponent) {
    const eraseComponent = node.parent
      ? node.parent.getScript(scripts.erase.name)
      : null
    if (!eraseComponent) return false
    return eraseComponent.maskImages.uuid === node.uuid
  }
  return false
}

// 擦除组件自定义判断区域
export const isEraseCustomJudgeAreas = (node: qc.Node): boolean => {
  const iframeWindow = gameIframe.iframe
  if (iframeWindow && iframeWindow.ps && iframeWindow.ps.EraseComponent) {
    const eraseComponent = node.parent
      ? node.parent.getScript(scripts.erase.name)
      : null
    if (!eraseComponent) return false
    return eraseComponent.customJudgeAreas.uuid === node.uuid
  }
  return false
}

// 拼图组件
export const isPuzzleNode = (node: qc.Node): boolean => {
  if (!node) {
    return false
  }
  const iframeWindow = gameIframe.iframe
  if (iframeWindow && iframeWindow.ps && iframeWindow.ps.PuzzleComponent) {
    const puzzle = node.getScript(scripts.puzzle.name)
    if (puzzle) {
      return true
    }
  }
  return false
}
// 是否2d玩法组件
export const is2dComponentNode = (node: qc.Node): boolean => {
  return (
    isQuickLayout(node) ||
    isDrawLineNode(node) ||
    isEraseNode(node) ||
    isParkNode(node) ||
    isMaskComponentNode(node) ||
    isCarouselNode(node) ||
    isPuzzleNode(node) ||
    isShelvesNode(node)
  )
}
// 获取场景里的快捷布局类型
export const getQuickLayoutComponent = (sceneNode: qc.Node): string | null => {
  if (!sceneNode) return null
  const children = getDescendants(sceneNode)
  for (const i in children) {
    const node = children[i]
    // eslint-disable-next-line no-continue
    if (!node) continue
    if (isParkNode(node)) {
      return quickLayoutComponent.park
    } else if (isPuzzleNode(node)) {
      return quickLayoutComponent.puzzle
    } else if (isShelvesNode(node)) {
      return quickLayoutComponent.shelves
    } else if (isQuickLayout(node)) {
      return quickLayoutComponent.grid
    }
  }
  return null
}
// 是否快捷布局组件
export const isQuickLayoutComponent = (node: qc.Node): boolean => {
  return (
    isQuickLayout(node) ||
    isParkNode(node) ||
    isPuzzleNode(node) ||
    isShelvesNode(node)
  )
}
export const isLayoutComponent = (node: qc.Node): boolean => {
  return isParkNode(node) || isPuzzleNode(node) || isShelvesNode(node)
}

/**
 * 祖先节点是否包含布局组件根节点
 * @param node
 */
export function hasLayoutComponentParent(node: qc.Node) {
  const list: [qc.Node] = getAllParentsBeforeScene(node) as [qc.Node]
  return list && list.some((node) => isQuickLayoutComponent(node))
}

// 获取场景中的快捷布局组件
export const getQuickLayoutComponentNode = (
  sceneNode: qc.Node,
): qc.Node | null => {
  if (!sceneNode) return null
  const children = getDescendants(sceneNode)
  for (const i in children) {
    const node = children[i]
    // eslint-disable-next-line no-continue
    if (!node) continue
    if (isQuickLayoutComponent(node)) {
      return node
    }
  }
  return null
}
// 是否快捷布局等2d玩法组件的子节点，用于项目资产中替换裁剪删除操作的限制
export function is2dComponentSubNodeForProjectResRestrict(node: qc.Node) {
  // eslint-disable-next-line no-constant-condition
  while (true) {
    if (!node) {
      return false
    } else if (node.getScript('ps.VPGamePlay')) {
      return false
    } else if (isQuickLayoutComponent(node)) {
      return true
    }
    node = node.parent
  }
}
// 协同流程制作的项目禁用精简layout
export const isCooperateProjectDisableMiniLayout = () => {
  const mc = gameIframe.UIRoot.getScript('ps.MainConfig')
  return !!mc.disableMiniLayout
}
